export interface RoleCapability {
  code: string;
  label: string;
  path?: string;
}

export const ADMIN_STAFF_META = {
  title: 'Admin & Staff — Full Function Reference',
  subtitle: 'Complete capability breakdown for Super Admin, Client Admin, and Employee roles',
};

export const SUPER_ADMIN_CAPABILITIES: RoleCapability[] = [
  { code: 'T', label: 'Manage all tenants (create / suspend / config)', path: '/tenants' },
  { code: 'S', label: 'Platform services builder (add new services)', path: '/platform/services' },
  { code: 'M', label: 'Master data builder (global masters)', path: '/platform/master-builder' },
  { code: 'P', label: 'Global permission & role templates', path: '/config/roles' },
  { code: 'A', label: 'Full audit log visibility across all tenants', path: '/audit-logs' },
  { code: 'B', label: 'Subscription plan management', path: '/subscriptions' },
  { code: 'R', label: 'Platform-wide revenue reports', path: '/reports' },
  { code: 'G', label: 'Payment gateway global config', path: '/config/payments' },
];

export const CLIENT_ADMIN_CAPABILITIES: RoleCapability[] = [
  { code: 'D', label: 'Full analytics dashboard', path: '/dashboard' },
  { code: 'U', label: 'User management (customers + partners)', path: '/users' },
  { code: 'Z', label: 'Zone setup with polygon + fare matrix', path: '/zones' },
  { code: 'S', label: 'Service configuration (enable / disable)', path: '/config/services' },
  { code: 'P', label: 'Promo codes + loyalty configuration', path: '/config/promos' },
  { code: 'O', label: 'Dispatcher operations (live rides / orders)', path: '/dispatcher' },
  { code: 'K', label: 'Partner KYC approval', path: '/config/documents' },
  { code: 'F', label: 'Finance: settlements + reports', path: '/settlements' },
  { code: 'N', label: 'Notification templates', path: '/config/notifications' },
  { code: 'G', label: 'Payment & SMS gateway config', path: '/config/payments' },
];

export const EMPLOYEE_CAPABILITIES: RoleCapability[] = [
  { code: 'D', label: 'Operations dashboard (permission-gated)', path: '/dashboard' },
  { code: 'O', label: 'Dispatcher — live rides & orders', path: '/dispatcher' },
  { code: 'Z', label: 'Zones & pricing (view / edit if permitted)', path: '/zones' },
  { code: 'K', label: 'Partner KYC & document review', path: '/config/documents' },
  { code: 'H', label: 'Service hubs — transport, delivery, on-demand', path: '/hubs/transport' },
  { code: 'R', label: 'Reports & settlements (if permitted)', path: '/reports' },
];

export interface HubTabReference {
  tab: string;
  whatItShows: string;
  keyActions: string;
  path: string;
}

export const TRANSPORT_HUB_TABS: HubTabReference[] = [
  {
    tab: 'Summary',
    whatItShows: 'Service KPIs, charts, and end-to-end flow guide',
    keyActions: 'Date filters, Open End-to-End Flow',
    path: '/hubs/transport/transport/summary',
  },
  {
    tab: 'Rides',
    whatItShows: 'All rides by status',
    keyActions: 'View, Assign driver, Cancel, Refund',
    path: '/hubs/transport/transport/bookings',
  },
  {
    tab: 'Book Ride',
    whatItShows: 'Manual / phone booking for customers',
    keyActions: 'Create ride, Assign driver',
    path: '/hubs/transport/transport/manual-booking',
  },
  {
    tab: 'Bidding',
    whatItShows: 'Bidding ride requests',
    keyActions: 'Configure bidding zone & rules',
    path: '/hubs/transport/transport/bidding',
  },
  {
    tab: 'Reviews',
    whatItShows: 'Driver & ride ratings',
    keyActions: 'Respond, Flag, Remove',
    path: '/hubs/transport/transport/reviews',
  },
  {
    tab: 'Earnings',
    whatItShows: 'Revenue per ride, commissions',
    keyActions: 'Export, Filter by date / driver',
    path: '/hubs/transport/transport/earnings',
  },
  {
    tab: 'Users',
    whatItShows: 'Customers using this service',
    keyActions: 'View profile, Block / unblock',
    path: '/hubs/transport/transport/users',
  },
  {
    tab: 'Drivers',
    whatItShows: 'Drivers assigned to this service',
    keyActions: 'Enable / Disable, View profile, KYC',
    path: '/hubs/transport/transport/partners',
  },
  {
    tab: 'Zone Pricing',
    whatItShows: 'Zone fare matrix for this service',
    keyActions: 'Edit base / per KM / surge rules',
    path: '/hubs/transport/transport/zones',
  },
  {
    tab: 'Zone Earnings',
    whatItShows: 'Driver payout rules by zone',
    keyActions: 'Commission %, incentives',
    path: '/hubs/transport/transport/partner-payout',
  },
  {
    tab: 'Rental packages',
    whatItShows: 'Hourly / package rental masters',
    keyActions: 'Add / edit packages',
    path: '/hubs/transport/transport/fleet',
  },
  {
    tab: 'Outstation routes',
    whatItShows: 'Intercity route & fare masters',
    keyActions: 'Add / edit routes',
    path: '/hubs/transport/transport/routes',
  },
  {
    tab: 'Masters',
    whatItShows: 'Vehicle, availability, and service masters',
    keyActions: 'Add / edit master records',
    path: '/hubs/transport/transport/masters',
  },
  {
    tab: 'Settings',
    whatItShows: 'Service-level config',
    keyActions: 'Commission, dispatch mode, OTP rules',
    path: '/hubs/transport/transport/settings',
  },
];

export const FOOD_DELIVERY_HUB_TABS: HubTabReference[] = [
  {
    tab: 'Summary',
    whatItShows: 'Order KPIs, charts, and end-to-end flow guide',
    keyActions: 'Date filters, Open End-to-End Flow',
    path: '/hubs/delivery/food-delivery/summary',
  },
  {
    tab: 'Live Orders',
    whatItShows: 'Active food orders in progress',
    keyActions: 'View, Manual assign, Cancel, Refund',
    path: '/hubs/delivery/food-delivery/bookings',
  },
  {
    tab: 'Order History',
    whatItShows: 'Delivered, cancelled, and refunded orders',
    keyActions: 'View details, Export',
    path: '/hubs/delivery/food-delivery/order-history',
  },
  {
    tab: 'Scheduled Orders',
    whatItShows: 'Upcoming orders scheduled for later',
    keyActions: 'View, Reschedule, Cancel',
    path: '/hubs/delivery/food-delivery/scheduled-orders',
  },
  {
    tab: 'Manual Order',
    whatItShows: 'Admin-created food orders',
    keyActions: 'Place order, Assign partner',
    path: '/hubs/delivery/food-delivery/manual-booking',
  },
  {
    tab: 'Restaurants',
    whatItShows: 'All merchants in this service',
    keyActions: 'Approve, Suspend, Edit commission',
    path: '/hubs/delivery/food-delivery/catalog',
  },
  {
    tab: 'Users',
    whatItShows: 'Customers ordering food',
    keyActions: 'View profile, Block / unblock',
    path: '/hubs/delivery/food-delivery/users',
  },
  {
    tab: 'Delivery Partners',
    whatItShows: 'Active delivery partners',
    keyActions: 'Assign, Performance view',
    path: '/hubs/delivery/food-delivery/partners',
  },
  {
    tab: 'Zones & Coverage',
    whatItShows: 'Delivery coverage polygons',
    keyActions: 'Edit zones, Enable service',
    path: '/hubs/delivery/food-delivery/zones',
  },
  {
    tab: 'Reviews',
    whatItShows: 'Restaurant + delivery ratings',
    keyActions: 'Moderate, Respond',
    path: '/hubs/delivery/food-delivery/reviews',
  },
  {
    tab: 'Earnings',
    whatItShows: 'Platform revenue + settlements',
    keyActions: 'Export, Trigger settlement',
    path: '/hubs/delivery/food-delivery/earnings',
  },
  {
    tab: 'Delivery Fees',
    whatItShows: 'Delivery fee slots + surge',
    keyActions: 'Edit per zone',
    path: '/hubs/delivery/food-delivery/pricing',
  },
  {
    tab: 'Masters',
    whatItShows: 'Cuisine, dietary, and food masters',
    keyActions: 'Add / edit master records',
    path: '/hubs/delivery/food-delivery/masters',
  },
  {
    tab: 'Settings',
    whatItShows: 'Commission, OTP, dispatch',
    keyActions: 'Configure per service',
    path: '/hubs/delivery/food-delivery/settings',
  },
];

export const GROCERY_DELIVERY_HUB_TABS: HubTabReference[] = [
  {
    tab: 'Summary',
    whatItShows: 'Order KPIs, charts, and end-to-end flow guide',
    keyActions: 'Date filters, Open End-to-End Flow',
    path: '/hubs/delivery/grocery-delivery/summary',
  },
  {
    tab: 'Live Orders',
    whatItShows: 'Active grocery orders in progress',
    keyActions: 'View, Manual assign, Cancel, Refund',
    path: '/hubs/delivery/grocery-delivery/bookings',
  },
  {
    tab: 'Order History',
    whatItShows: 'Delivered, cancelled, and refunded orders',
    keyActions: 'View details, Export',
    path: '/hubs/delivery/grocery-delivery/order-history',
  },
  {
    tab: 'Manual Order',
    whatItShows: 'Admin-created grocery orders',
    keyActions: 'Place order, Assign partner',
    path: '/hubs/delivery/grocery-delivery/manual-booking',
  },
  {
    tab: 'Stores & Products',
    whatItShows: 'Grocery stores and catalog',
    keyActions: 'Approve store, Manage products',
    path: '/hubs/delivery/grocery-delivery/catalog',
  },
  {
    tab: 'Users',
    whatItShows: 'Customers ordering grocery',
    keyActions: 'View profile, Block / unblock',
    path: '/hubs/delivery/grocery-delivery/users',
  },
  {
    tab: 'Delivery Partners',
    whatItShows: 'Active delivery partners',
    keyActions: 'Assign, Performance view',
    path: '/hubs/delivery/grocery-delivery/partners',
  },
  {
    tab: 'Zones & Coverage',
    whatItShows: 'Delivery coverage + slots',
    keyActions: 'Edit zones, Enable service',
    path: '/hubs/delivery/grocery-delivery/zones',
  },
  {
    tab: 'Reviews',
    whatItShows: 'Store + delivery ratings',
    keyActions: 'Moderate, Respond',
    path: '/hubs/delivery/grocery-delivery/reviews',
  },
  {
    tab: 'Earnings',
    whatItShows: 'Platform revenue + settlements',
    keyActions: 'Export, Trigger settlement',
    path: '/hubs/delivery/grocery-delivery/earnings',
  },
  {
    tab: 'Delivery Fees',
    whatItShows: 'Delivery fee slots + surge',
    keyActions: 'Edit per zone',
    path: '/hubs/delivery/grocery-delivery/pricing',
  },
  {
    tab: 'Masters',
    whatItShows: 'Warehouse, supplier, and grocery masters',
    keyActions: 'Add / edit master records',
    path: '/hubs/delivery/grocery-delivery/masters',
  },
  {
    tab: 'Settings',
    whatItShows: 'Commission, OTP, dispatch',
    keyActions: 'Configure per service',
    path: '/hubs/delivery/grocery-delivery/settings',
  },
];

export const ON_DEMAND_HUB_TABS: HubTabReference[] = [
  {
    tab: 'Masters',
    whatItShows: 'Service categories, skills, visit charges, and pricing templates',
    keyActions: 'Add electrician / plumber categories, skills, visit fees',
    path: '/hubs/on_demand/home-services/masters',
  },
  {
    tab: 'Summary',
    whatItShows: 'Booking KPIs, charts, and home-services flow guide',
    keyActions: 'Date filters, review tab reference',
    path: '/hubs/on_demand/home-services/summary',
  },
  {
    tab: 'Bookings',
    whatItShows: 'All on-demand service bookings by status',
    keyActions: 'View, Assign provider, Update status, Cancel',
    path: '/hubs/on_demand/home-services/bookings',
  },
  {
    tab: 'Manual Booking',
    whatItShows: 'Admin-created home service bookings',
    keyActions: 'Book electrician, plumber, cleaning, etc.',
    path: '/hubs/on_demand/home-services/manual-booking',
  },
  {
    tab: 'Packages',
    whatItShows: 'Service packages with price, duration, skills',
    keyActions: 'Add AC service, wiring fix, cleaning packages',
    path: '/hubs/on_demand/home-services/catalog',
  },
  {
    tab: 'Users',
    whatItShows: 'Customers who book services',
    keyActions: 'View profile, Block / unblock',
    path: '/hubs/on_demand/home-services/users',
  },
  {
    tab: 'Service Providers',
    whatItShows: 'Electricians, plumbers, cleaners, and other providers',
    keyActions: 'Enable / Disable, View profile, KYC',
    path: '/hubs/on_demand/home-services/partners',
  },
  {
    tab: 'Settings',
    whatItShows: 'Commission, visit charge, and dispatch rules',
    keyActions: 'Configure per service',
    path: '/hubs/on_demand/home-services/settings',
  },
];

/** Hub tab cheat-sheet for End-to-End Flow drawers and admin reference */
export function getServiceHubTabReference(
  hub: 'transport' | 'delivery' | 'on_demand',
  slug?: string,
): { title: string; rows: HubTabReference[] } | null {
  if (hub === 'on_demand' || slug === 'home-services') {
    return { title: 'Hub tab reference — Services', rows: ON_DEMAND_HUB_TABS };
  }
  if (hub === 'transport') {
    return { title: 'Hub tab reference — Transport', rows: TRANSPORT_HUB_TABS };
  }
  if (slug === 'grocery-delivery') {
    return { title: 'Hub tab reference — Grocery Delivery', rows: GROCERY_DELIVERY_HUB_TABS };
  }
  if (slug === 'food-delivery' || hub === 'delivery') {
    return { title: 'Hub tab reference — Food Delivery', rows: FOOD_DELIVERY_HUB_TABS };
  }
  return null;
}

export type AdminStaffRole = 'super_admin' | 'client_admin' | 'employee';

/** Map API / legacy userType values to admin-staff roles for the reference guide */
export function resolveAdminStaffRole(userType?: string): AdminStaffRole | null {
  const t = (userType || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (t === 'super_admin' || t === 'superadmin') return 'super_admin';
  if (t === 'client_admin' || t === 'clientadmin' || t === 'admin') return 'client_admin';
  if (t === 'employee' || t === 'staff' || t === 'dispatch_staff') return 'employee';
  return null;
}

export function isAdminStaffUserType(userType?: string): boolean {
  return resolveAdminStaffRole(userType) !== null;
}

export function getAdminStaffCapabilities(role: AdminStaffRole): RoleCapability[] {
  switch (role) {
    case 'super_admin':
      return [...SUPER_ADMIN_CAPABILITIES, ...CLIENT_ADMIN_CAPABILITIES];
    case 'client_admin':
      return CLIENT_ADMIN_CAPABILITIES;
    case 'employee':
      return EMPLOYEE_CAPABILITIES;
    default:
      return [];
  }
}

export function getCapabilitySections(role: AdminStaffRole) {
  if (role === 'super_admin') {
    return [
      { title: 'Super Admin', items: SUPER_ADMIN_CAPABILITIES, accent: '#7c3aed' },
      { title: 'Client Admin (within tenant)', items: CLIENT_ADMIN_CAPABILITIES, accent: '#2563eb' },
    ];
  }
  if (role === 'client_admin') {
    return [{ title: 'Client Admin', items: CLIENT_ADMIN_CAPABILITIES, accent: '#2563eb' }];
  }
  return [{ title: 'Employee (Staff)', items: EMPLOYEE_CAPABILITIES, accent: '#0ea5e9' }];
}
